import { createStore, createEvent, createEffect, combine } from 'effector';
import request from '@helpers/request';
import api from '@constants/endpoints';
import createPreloadersStore from './preloaders';
import IComment from '@interfaces/IComment';
import { get } from 'lodash';
import { AxiosResponse } from 'axios';
import { DEFAULT_PAGE_SIZE } from '@constants/pagination';
import update from 'immutability-helper';

const defaultCommentsFilters = {
  sort: 'whenCommented,desc',
  visibleBlockPost: 'ALL',
};

export const reset = createEvent('Comments store reset');

interface IGetCommentsParams {
  commentId: IComment['id'];
  preloaderId: IComment['id'];
}

export const getComments = createEffect({
  name: 'getComments',
  async handler({ commentId }: IGetCommentsParams) {
    const createdStore: ICommentStore | null = get(comments.getState(), ['list', commentId], null);
    const page: number = createdStore ? createdStore.pagination.current + 1 : 0;
    const response = await request(api.comments.getListForComment, {
      urlParams: { commentId },
      params: {
        paged: true,
        size: DEFAULT_PAGE_SIZE,
        page,
        ...defaultCommentsFilters,
      },
    });
    return response;
  },
});

export const block = createEffect({
  name: 'block',
  async handler({
    commentId,
  }: {
    commentId: IComment['id'];
    isSubcomment?: boolean;
    preloaderId: string;
  }) {
    const response = await request(api.comments.block, {
      urlParams: { commentId },
    });
    return response;
  },
});

export const unblock = createEffect({
  name: 'unblock',
  async handler({
    commentId,
  }: {
    commentId: IComment['id'];
    isSubcomment?: boolean;
    preloaderId: string;
  }) {
    const response = await request(api.comments.unblock, {
      urlParams: { commentId },
    });
    return response;
  },
});

const preloaders = createPreloadersStore([getComments, block, unblock]);

interface ICommentStore {
  list: IComment[];
  pagination: {
    current: number;
    total: number;
    pageSize: number;
  };
}

const updateItemHandler = (
  state: Record<IComment['id'], ICommentStore>,
  { result: { data } }: { result: AxiosResponse },
) => {
  const { parentCommentId } = data;
  const newState = { ...state };
  if (parentCommentId) {
    const updatedCommentIndex = newState[parentCommentId].list.findIndex((c) => c.id === data.id);
    if (updatedCommentIndex >= 0) {
      newState[parentCommentId].list = update(state[parentCommentId].list, {
        $splice: [[updatedCommentIndex, 1, data]],
      });
    }
  }
  return newState;
};

const bodyPath = ['data'];

const getCommentsHandler = (
  state: Record<IComment['id'], ICommentStore>,
  { result, params: { commentId } }: { params: IGetCommentsParams; result: AxiosResponse },
) => {
  if (!state[commentId]) {
    return {
      ...state,
      [commentId]: {
        list: get(result, [...bodyPath, 'content'], []),
        pagination: {
          current: get(result, [...bodyPath, 'number'], 0),
          total: get(result, [...bodyPath, 'totalElements'], 0),
          pageSize: DEFAULT_PAGE_SIZE,
        },
      },
    };
  } else {
    return {
      ...state,
      [commentId]: {
        list: [...state[commentId].list, ...get(result, [...bodyPath, 'content'], [])],
        pagination: {
          current: get(result, [...bodyPath, 'number'], state[commentId].pagination.current + 1),
          total: get(
            result,
            [...bodyPath, 'totalElements'],
            state[commentId].pagination.current + 1,
          ),
          pageSize: DEFAULT_PAGE_SIZE,
        },
      },
    };
  }
};

const list = createStore<Record<IComment['id'], ICommentStore>>({})
  .on(getComments.done, getCommentsHandler)
  .on(block.done, updateItemHandler)
  .on(unblock.done, updateItemHandler)
  .reset(reset);

const comments = combine({ preloaders, list });

export default comments;
